import api from './api';
import { useConsentStore } from '../store/consentStore';
import { getVisitorId } from '../utils/visitorId';

export enum StoreEventType {
  PageView = 0,
  ProductView = 1,
  AddToCart = 2,
  RemoveFromCart = 3,
  Search = 4,
  CheckoutStep = 5,
  OrderPlaced = 6,
}

export interface StoreEventInput {
  type: StoreEventType;
  productId?: number;
  productVariantId?: number;
  searchTerm?: string;
  resultsCount?: number;
  path?: string;
  metadata?: Record<string, unknown>;
}

interface StoreEventPayload {
  type: StoreEventType;
  productId: number | null;
  productVariantId: number | null;
  searchTerm: string | null;
  resultsCount: number | null;
  path: string | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export interface CartSyncItem {
  productVariantId: number;
  quantity: number;
}

const AUTH_TOKEN_STORAGE_KEY = 'store_token';
const FLUSH_INTERVAL_MS = 5000;
const MAX_EVENTS_PER_REQUEST = 25;
const MAX_CART_ITEMS = 100;
const MAX_PATH_LENGTH = 300;
const MAX_SEARCH_TERM_LENGTH = 200;
const MAX_METADATA_LENGTH = 2000;

let eventQueue: StoreEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let lifecycleListenersRegistered = false;

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Visitor-Id': getVisitorId(),
  };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
  }
  return headers;
}

function sendBeacon(method: 'PUT' | 'POST', path: string, body: unknown): void {
  try {
    void fetch(`${api.defaults.baseURL}${path}`, {
      method,
      headers: buildHeaders(),
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
  }
}

function clearFlushTimer(): void {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }
}

function flushEvents(): void {
  clearFlushTimer();
  while (eventQueue.length > 0) {
    const batch = eventQueue.slice(0, MAX_EVENTS_PER_REQUEST);
    eventQueue = eventQueue.slice(MAX_EVENTS_PER_REQUEST);
    sendBeacon('POST', '/store/events', { events: batch });
  }
}

function registerLifecycleListeners(): void {
  if (lifecycleListenersRegistered || typeof window === 'undefined') return;
  lifecycleListenersRegistered = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents();
  });
  window.addEventListener('pagehide', flushEvents);
}

function sanitizeMetadata(metadata?: Record<string, unknown>): Record<string, unknown> {
  if (!metadata) return {};
  try {
    return JSON.stringify(metadata).length <= MAX_METADATA_LENGTH ? metadata : {};
  } catch {
    return {};
  }
}

export function trackStoreEvent(event: StoreEventInput): void {
  if (useConsentStore.getState().analytics !== true) return;

  registerLifecycleListeners();

  const path = event.path ?? window.location.pathname;
  eventQueue.push({
    type: event.type,
    productId: event.productId ?? null,
    productVariantId: event.productVariantId ?? null,
    searchTerm: event.searchTerm ? event.searchTerm.slice(0, MAX_SEARCH_TERM_LENGTH) : null,
    resultsCount: event.resultsCount ?? null,
    path: path.slice(0, MAX_PATH_LENGTH),
    metadata: sanitizeMetadata(event.metadata),
    occurredAt: new Date().toISOString(),
  });

  if (eventQueue.length >= MAX_EVENTS_PER_REQUEST) {
    flushEvents();
    return;
  }
  if (flushTimer === undefined) {
    flushTimer = setTimeout(flushEvents, FLUSH_INTERVAL_MS);
  }
}

export const trackingService = {
  syncCart(items: CartSyncItem[]): void {
    registerLifecycleListeners();
    sendBeacon('PUT', '/store/cart', { items: items.slice(0, MAX_CART_ITEMS) });
  },

  saveCartContact(name: string, phone: string): void {
    sendBeacon('POST', '/store/cart/contact', { name, phone });
  },
};
